import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Sparkles, 
  ArrowLeft, 
  RotateCcw, 
  Play, 
  Trophy, 
  Plus, 
  Trash2,
  CheckCircle2 
} from 'lucide-react';

export const RandomPickerWheel = () => {
  const { withAd, toast } = useAdRedirect();
  const [itemsInput, setItemsInput] = useState(`Alice\nBob\nCharlie\nDavid\nEmma\nFrank\nGrace`);
  const [itemsList, setItemsList] = useState(['Alice', 'Bob', 'Charlie', 'David', 'Emma', 'Frank', 'Grace']);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winner, setWinner] = useState(null);

  const colors = ['#0575fb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

  const parseItems = (e) => {
    if (e) e.preventDefault();
    const parsed = itemsInput.split('\n').map(i => i.trim()).filter(Boolean);
    if (parsed.length < 2) {
      alert('Please enter at least 2 choices!');
      return;
    }
    setItemsList(parsed);
    setWinner(null);
  };

  const spinWheel = () => {
    if (spinning || itemsList.length < 2) return;
    setSpinning(true);
    setWinner(null);

    // Random rotation
    const baseSpins = 360 * 5; // 5 full rounds
    const randomDeg = Math.floor(Math.random() * 360);
    const totalRotation = rotation + baseSpins + randomDeg;

    setRotation(totalRotation);

    // Calculate winning item
    setTimeout(() => {
      setSpinning(false);
      const normalizedDeg = totalRotation % 360;
      const sliceDeg = 360 / itemsList.length;

      // Pointer is at top (270 degrees in standard circle math, or 0 relative depending on CSS)
      // If 0 degree is top, then rotating clockwise shifts items counter-clockwise relative to pointer.
      const winningIndex = Math.floor((360 - normalizedDeg) / sliceDeg) % itemsList.length;
      setWinner(itemsList[winningIndex]);
    }, 4000);
  };

  const handleReset = () => {
    setItemsInput('');
    setItemsList([]);
    setWinner(null);
    setRotation(0);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Random Picker Wheel - Spin the Wheel Online" 
        description="Input names, choices, or giveaway contestants and spin the random picker wheel. Fun, interactive decision maker with confetti celebration."
        keywords="random picker wheel, spin the wheel, name wheel picker, giveaway winner picker, decision maker wheel, random choice generator"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Random Picker Wheel</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Random Picker Wheel & Decision Maker
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Enter a list of names, raffle winners, or dinner choices to spin our fair, animated picker wheel.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Choices Input */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={18} style={{ color: 'var(--primary)' }} /> Contestants / Options List
            </h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Clear List
            </button>
          </div>

          <form onSubmit={parseItems}>
            <textarea 
              rows="10"
              value={itemsInput} 
              onChange={(e) => setItemsInput(e.target.value)}
              placeholder="Alice&#10;Bob&#10;Charlie&#10;David"
              style={{ width: '100%', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6, marginBottom: '20px' }}
            />

            <button 
              type="submit" 
              style={{ width: '100%', background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '14px', borderRadius: '12px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'var(--transition-smooth)' }}
            >
              Update Wheel Choices ({itemsList.length})
            </button>
          </form>

          <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', marginTop: '20px' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Fair Spin Guarantee</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>We utilize advanced mathematical pseudo-random number generators to ensure unbiased and fully authentic raffle spin results.</div>
          </div>
        </div>

        {/* Right Side: Visual Spinning Wheel Dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '36px 24px', boxShadow: 'var(--shadow-sm)', textAlign: 'center', width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            
            {/* Winning Display Banner */}
            {winner && (
              <div style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 117, 251, 0.1) 100%)', padding: '24px', borderRadius: '16px', border: '1px solid #10b981', color: '#10b981', width: '100%', marginBottom: '28px', animation: 'bounce 0.5s ease' }}>
                <Trophy size={40} style={{ marginBottom: '8px' }} />
                <div style={{ fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>WINNER SELECTED!</div>
                <div style={{ fontSize: '2.4rem', fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1.1 }}>{winner}</div>
              </div>
            )}

            {/* Pointer / Indicator */}
            <div style={{ width: '0', height: '0', borderLeft: '16px solid transparent', borderRight: '16px solid transparent', borderTop: '24px solid var(--text-primary)', marginBottom: '-12px', zIndex: 10 }}></div>

            {/* Spinning Circle Box */}
            <div 
              style={{ 
                width: '320px', 
                height: '320px', 
                borderRadius: '50%', 
                border: '8px solid var(--text-primary)', 
                position: 'relative', 
                overflow: 'hidden', 
                transition: 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)', 
                transform: `rotate(${rotation}deg)`,
                boxShadow: 'var(--shadow-lg)'
              }}
            >
              {itemsList.length > 0 ? itemsList.map((item, idx) => {
                const sliceDeg = 360 / itemsList.length;
                const skewDeg = 90 - sliceDeg;
                const rotateDeg = sliceDeg * idx;
                const bgColor = colors[idx % colors.length];

                return (
                  <div 
                    key={idx} 
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '50%',
                      height: '50%',
                      transformOrigin: '0% 100%',
                      transform: `rotate(${rotateDeg}deg) skewY(${skewDeg}deg)`,
                      background: bgColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div 
                      style={{ 
                        transform: `skewY(-${skewDeg}deg) rotate(${sliceDeg / 2}deg)`,
                        position: 'absolute',
                        left: '20px',
                        bottom: '20px',
                        color: '#ffffff',
                        fontSize: '0.85rem',
                        fontWeight: 800,
                        textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                        maxWidth: '80px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item}
                    </div>
                  </div>
                );
              }) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-secondary)' }}>Add Choices</div>
              )}
            </div>

            {/* Spin Action Trigger */}
            <button 
              onClick={spinWheel} 
              disabled={spinning || itemsList.length < 2}
              style={{ marginTop: '36px', padding: '18px 48px', borderRadius: '16px', border: 'none', background: spinning ? '#cbd5e1' : '#10b981', color: '#ffffff', fontSize: '1.25rem', fontWeight: 900, cursor: spinning || itemsList.length < 2 ? 'not-allowed' : 'pointer', transition: 'var(--transition-smooth)', boxShadow: '0 10px 20px rgba(16, 185, 129, 0.3)' }}
            >
              {spinning ? 'SPINNING...' : 'SPIN WHEEL'}
            </button>

            <div style={{ marginTop: '24px' }}>
              <AdToast message={toast} />
              <button 
                onClick={withAd('picker-log-btn', () => alert("Raffle verification snapshot stored."))}
                style={{ background: 'rgba(15, 23, 42, 0.04)', color: 'var(--text-secondary)', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Log Raffle Snapshot
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="random-picker-wheel" />
    </div>
  );
};

export default RandomPickerWheel;
